import { useQueryClient } from '@tanstack/react-query';

import { logout } from '@/api/auth/logout';
import { APP_ROUTES } from '@/constants/app-routes';
import { claimRedirect } from '@/shared/api/redirect-to-sign-in';
import { toastApiError } from '@/shared/api/toast-api-error';
import { announceSession } from '@/shared/utils/session-channel';
import { writeSignedOut } from '@/shared/utils/signed-out-flag';
import { sessionCleared } from '@/store/auth.slice';
import { useAppDispatch } from '@/store/store';

/**
 * Ends the server session, then leaves the page entirely: to the identity
 * provider's end-session page when it gave one, otherwise to sign-in. A full
 * load rather than a route change, so nothing from the session survives in
 * memory and the redirect guard starts fresh.
 */
export const useSignOut = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return async () => {
    // Set before the request, so any redirect fired while it is in flight
    // lands on the sign-in page and not at the identity provider.
    writeSignedOut(true);

    let endSessionUrl: string | null;

    try {
      ({ endSessionUrl } = await logout());
    } catch (error) {
      // The server session is still alive, so claiming to be signed out would be a lie.
      writeSignedOut(false);
      toastApiError(error);
      return;
    }

    claimRedirect();
    announceSession('signed-out');
    dispatch(sessionCleared());
    queryClient.clear();

    window.location.assign(endSessionUrl ?? APP_ROUTES.login);
  };
};
